// Shapes of the GenAI AI Agents Service system API (genai-mic/agent/v1/system).
// Source of truth: genai-ai-agents-service/docs/CONTRACT.md and its dto package.

export type AdapterType = 'OPENAI_COMPATIBLE' | 'ANTHROPIC';
export type AgentStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED';
export type ChannelType = 'WEB_WIDGET' | 'WHATSAPP' | 'WEBHOOK';
/** Contract v1.6: a trigger run is a conversation with channel type TRIGGER (no channel row). */
export type ConversationChannelType = ChannelType | 'TRIGGER';
export type ChannelMode = 'AI' | 'ASSIST';
export type ConversationStatus = 'OPEN' | 'CLOSED';
export type MessageRole = 'USER' | 'ASSISTANT';

export const ADAPTER_TYPES: AdapterType[] = ['OPENAI_COMPATIBLE', 'ANTHROPIC'];
export const AGENT_STATUSES: AgentStatus[] = ['DRAFT', 'ACTIVE', 'PAUSED'];
export const CHANNEL_MODES: ChannelMode[] = ['AI', 'ASSIST'];

export interface Provider {
  id: string;
  code: string;
  displayName: string;
  adapterType: AdapterType;
  baseUrl: string;
  enabled: boolean;
  hasApiKey: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Create: code, displayName, adapterType, baseUrl required. Update: null fields stay unchanged. */
export interface ProviderRequest {
  code?: string | null;
  displayName?: string | null;
  adapterType?: AdapterType | null;
  baseUrl?: string | null;
  enabled?: boolean | null;
}

export interface LlmModel {
  id: string;
  providerId: string;
  modelId: string;
  displayName: string;
  contextWindow: number | null;
  maxOutputTokens: number | null;
  supportsTemperature: boolean;
  inputPricePer1M: number | null;
  outputPricePer1M: number | null;
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
  /** Thinking levels (reasoning_effort) the provider accepts for this model; [] = none (contract v1.2). */
  reasoningEfforts?: string[] | null;
  /** Sent when an agent keeps "Model default"; null = nothing is sent. */
  defaultReasoningEffort?: string | null;
  /** The model accepts images (vision); needed for agents with image input (contract v1.2). */
  supportsVision?: boolean | null;
  /** Contract v1.4: the model can call tools (playbooks); false = no tools and no playbook section are sent. */
  supportsTools?: boolean | null;
}

/** Every reasoning_effort value the service accepts in a model's list, from fastest to deepest. */
export const REASONING_EFFORT_VALUES: string[] = ['none', 'minimal', 'low', 'medium', 'high', 'xhigh'];

/** What the customer Dashboard calls the levels (other values keep their own name). */
export const REASONING_EFFORT_LABELS: Record<string, string> = { low: 'Fast (low)', medium: 'Balanced (medium)', high: 'Deep (high)' };

/** Agent pictures (contract v1.2): PNG, JPEG or WebP up to 2 MB; the service checks the bytes again. */
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** 'type' | 'size' | 'empty' | null for a picked picture. */
export function avatarFileProblem(file: { size: number; type?: string; name?: string } | null | undefined): 'type' | 'size' | 'empty' | null {
  if (!file || !file.size) return 'empty';
  const type = (file.type || '').toLowerCase();
  const byName = /\.(png|jpe?g|webp)$/i.test(file.name || '');
  if (type ? !AVATAR_TYPES.includes(type) : !byName) return 'type';
  return file.size > AVATAR_MAX_BYTES ? 'size' : null;
}

/** Create: providerId, modelId, displayName required. Update: null fields stay unchanged. */
export interface ModelRequest {
  providerId?: string | null;
  modelId?: string | null;
  displayName?: string | null;
  contextWindow?: number | null;
  maxOutputTokens?: number | null;
  supportsTemperature?: boolean | null;
  inputPricePer1M?: number | null;
  outputPricePer1M?: number | null;
  enabled?: boolean | null;
  /** [] clears the list; null keeps it. */
  reasoningEfforts?: string[] | null;
  /** '' clears the default; null keeps it. Must be one of reasoningEfforts. */
  defaultReasoningEffort?: string | null;
  supportsVision?: boolean | null;
  /** Contract v1.4; null keeps it. */
  supportsTools?: boolean | null;
}

// ------------------------------------------------------------ capability models (contract v1.2, section 0)

export type Capability = 'TRANSCRIPTION' | 'SPEECH' | 'EMBEDDING';
export const CAPABILITIES: Capability[] = ['TRANSCRIPTION', 'SPEECH', 'EMBEDDING'];
export const CAPABILITY_LABELS: Record<Capability, string> = {
  TRANSCRIPTION: 'Transcription (speech to text)',
  SPEECH: 'Speech (text to speech)',
  EMBEDDING: 'Embedding (knowledge files)',
};

/** SPEECH: {voices, defaultVoice, format}; EMBEDDING: {dimensions}; other keys are kept as they are. */
export interface CapabilityModelOptions {
  voices?: string[];
  defaultVoice?: string;
  format?: string;
  dimensions?: number;
  [key: string]: unknown;
}

export interface CapabilityModel {
  id: string;
  capability: Capability;
  providerId: string;
  /** Sent by the service for the list. */
  providerDisplayName?: string | null;
  modelId: string;
  displayName: string;
  options: CapabilityModelOptions | null;
  /** One default per capability. Normalized from `isDefault` or Jackson's `default`. */
  isDefault: boolean;
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Create: capability, providerId, modelId, displayName required. Update: null fields stay unchanged. */
export interface CapabilityModelRequest {
  capability?: Capability | null;
  providerId?: string | null;
  modelId?: string | null;
  displayName?: string | null;
  options?: CapabilityModelOptions | null;
  isDefault?: boolean | null;
  enabled?: boolean | null;
}

/** The service may name the boolean `isDefault` or (Jackson bean naming) `default`; the UI always uses isDefault. */
export function normalizeCapabilityModel(raw: any): CapabilityModel {
  const r = raw || {};
  const flag = r.isDefault !== undefined && r.isDefault !== null ? r.isDefault : r.default;
  const { default: _ignored, ...rest } = r;
  return { ...rest, options: r.options ?? null, isDefault: flag === true, enabled: r.enabled !== false } as CapabilityModel;
}

export interface AgentIdentity {
  role: string | null;
  identity: string | null;
  mission: string | null;
  scope: string | null;
  languageBehavior: string | null;
  behaviorRules: string | null;
  hardRules: string | null;
  responseStyle: string | null;
  knowledge: string | null;
}

export const IDENTITY_SECTIONS: { key: keyof AgentIdentity; label: string; hint: string; rows: number }[] = [
  { key: 'role', label: 'Role', hint: 'What the agent is, in one line (e.g. booking assistant of the clinic).', rows: 2 },
  { key: 'identity', label: 'Identity', hint: 'Who the agent speaks for and how it presents itself.', rows: 4 },
  { key: 'mission', label: 'Mission', hint: 'What the agent must achieve for the visitor and the business.', rows: 4 },
  { key: 'scope', label: 'Scope', hint: 'Topics the agent covers, and what is out of scope.', rows: 4 },
  { key: 'languageBehavior', label: 'Language behavior', hint: 'Languages and dialect; answer in the visitor\'s language.', rows: 3 },
  { key: 'behaviorRules', label: 'Behavior rules', hint: 'How the agent behaves in the conversation.', rows: 5 },
  { key: 'hardRules', label: 'Hard rules', hint: 'Rules that must never be broken. Added last to the prompt.', rows: 5 },
  { key: 'responseStyle', label: 'Response style', hint: 'Length, tone and format of the answers.', rows: 3 },
  { key: 'knowledge', label: 'Knowledge', hint: 'Facts the agent may use: services, prices, hours, addresses, FAQs.', rows: 18 },
];

export function emptyIdentity(): AgentIdentity {
  return {
    role: null,
    identity: null,
    mission: null,
    scope: null,
    languageBehavior: null,
    behaviorRules: null,
    hardRules: null,
    responseStyle: null,
    knowledge: null,
  };
}

export interface Agent {
  id: string;
  projectId: string;
  name: string;
  avatarUrl: string | null;
  modelId: string;
  temperature: number | null;
  maxOutputTokens: number | null;
  historyLimit: number;
  status: AgentStatus;
  welcomeMessage: string | null;
  fallbackMessage: string | null;
  createdAt?: string;
  updatedAt?: string;
  identity?: AgentIdentity | null;
  /** Thinking level; null = the model default (contract v1.2). */
  reasoningEffort?: string | null;
  /** Wait after a user message before answering, 0..10000 ms (default 1500). */
  replyDelayMs?: number | null;
  imageInputEnabled?: boolean | null;
  voiceInputEnabled?: boolean | null;
  voiceOutputEnabled?: boolean | null;
  /** One of the default SPEECH model's voices; null = its defaultVoice. */
  voiceName?: string | null;
  knowledgeFilesEnabled?: boolean | null;
  /** Contract v1.3: visitors may attach documents in the chat. */
  documentInputEnabled?: boolean | null;
}

export const REPLY_DELAY_DEFAULT_MS = 1500;
export const REPLY_DELAY_MAX_MS = 10000;

/** Create: projectId, name, modelId required. Update: null fields stay unchanged (projectId cannot change). */
export interface AgentRequest {
  projectId?: string | null;
  name?: string | null;
  avatarUrl?: string | null;
  modelId?: string | null;
  temperature?: number | null;
  maxOutputTokens?: number | null;
  historyLimit?: number | null;
  status?: AgentStatus | null;
  welcomeMessage?: string | null;
  fallbackMessage?: string | null;
  identity?: AgentIdentity | null;
  /** One of the model's reasoningEfforts; '' = model default; null = unchanged. */
  reasoningEffort?: string | null;
  replyDelayMs?: number | null;
  imageInputEnabled?: boolean | null;
  voiceInputEnabled?: boolean | null;
  voiceOutputEnabled?: boolean | null;
  /** '' = the SPEECH model's default voice; null = unchanged. */
  voiceName?: string | null;
  knowledgeFilesEnabled?: boolean | null;
  documentInputEnabled?: boolean | null;
}

/** Message for a failed agent save; a 409 FEATURE_NOT_IN_PLAN gets a clear explanation. */
export function agentSaveErrorMessage(err: any, fallback: string): string {
  const body = err?.error || {};
  const code = String(body.code || body.error || '');
  const message = String(body.message || '');
  if (err?.status === 409 && (code === 'FEATURE_NOT_IN_PLAN' || message.includes('FEATURE_NOT_IN_PLAN'))) {
    const detail = message && message !== 'FEATURE_NOT_IN_PLAN' ? ` (${message})` : '';
    return `The project plan does not allow this feature${detail}. Allow it on the Project plans page first, or switch it off here.`;
  }
  return message || fallback;
}

export interface ChannelSettings {
  title?: string;
  primaryColor?: string;
  position?: string;
  launcherLabel?: string;
  [key: string]: unknown;
}

export interface Channel {
  id: string;
  agentId: string;
  projectId: string;
  type: ChannelType;
  publicKey: string;
  allowedOrigins: string[];
  mode: ChannelMode;
  settings: ChannelSettings | null;
  enabled: boolean;
  embedSnippet: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChannelRequest {
  type: ChannelType;
  allowedOrigins: string[];
  mode: ChannelMode;
  settings: ChannelSettings;
  enabled: boolean;
}

export interface Conversation {
  id: string;
  projectId: string;
  agentId: string;
  channelId: string | null;
  channelType: ConversationChannelType | null;
  externalUserKey: string | null;
  status: ConversationStatus;
  startedAt: string;
  lastMessageAt: string | null;
  messageCount: number;
}

export interface ConversationMessage {
  id: string;
  conversationId: string;
  role: MessageRole;
  content: string;
  modelId: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  latencyMs: number | null;
  finishReason: string | null;
  failed: boolean | null;
  error: string | null;
  createdAt: string;
  /** Images and voice recordings of the message (contract v1.2). */
  attachments?: MessageAttachment[] | null;
  /** Contract v1.4: the playbooks read for this answer (assistant messages); [] when none. */
  playbooksUsed?: PlaybookRef[] | null;
  /** Contract v1.5: the MCP tool steps of this answer (assistant messages); [] when none. */
  toolsUsed?: ToolUse[] | null;
}

/** One MCP tool step of an answer (contract v1.5, `message.tools_used`). */
export interface ToolUse {
  service: string;
  tool: string;
  /** OK, TOOL_ERROR, DENIED, TIMEOUT, NEEDS_CONFIRMATION, CONFIRMED, CANCELLED, REFUSED. */
  outcome: string;
}

/** Readable words for the tool outcomes that are not a plain success (contract v1.5, section 4). */
export const TOOL_OUTCOME_LABELS: Record<string, string> = {
  OK: '',
  TOOL_ERROR: 'error',
  DENIED: 'denied',
  TIMEOUT: 'timed out',
  NEEDS_CONFIRMATION: 'waiting for confirmation',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  REFUSED: 'refused',
};

/** A tool chip: the tool name, its service and a readable outcome ('' for OK). */
export interface ToolChip {
  service: string;
  tool: string;
  outcome: string;
  outcomeLabel: string;
  /** Not a success: shown in a warning colour. */
  problem: boolean;
}

/** The "Tool: <tool>" chips of a message: only assistant messages; entries without a tool name are skipped. */
export function toolsOf(m: Pick<ConversationMessage, 'role' | 'toolsUsed'>): ToolChip[] {
  if (m.role !== 'ASSISTANT' || !Array.isArray(m.toolsUsed)) return [];
  return m.toolsUsed
    .filter((t) => t && t.tool)
    .map((t) => {
      const outcome = String(t.outcome || '').toUpperCase();
      const label = outcome in TOOL_OUTCOME_LABELS ? TOOL_OUTCOME_LABELS[outcome] : outcome.toLowerCase();
      return {
        service: String(t.service || ''),
        tool: String(t.tool),
        outcome,
        outcomeLabel: label,
        problem: ['TOOL_ERROR', 'DENIED', 'TIMEOUT', 'REFUSED'].includes(outcome),
      };
    });
}

// ------------------------------------------------------------ AI employee and tool connections (contract v1.5, sections 1 and 3)

/** GET /system/agents/{id}/service-account: never the key itself. */
export interface ServiceAccount {
  connected: boolean;
  authUserId: string | null;
  name: string | null;
  keyPrefix: string | null;
  connectedAt: string | null;
  /** The last key exchange worked; null = not tried yet. */
  valid: boolean | null;
}

/** One tool of a service, from its live tools/list. */
export interface ToolInfo {
  name: string;
  title: string | null;
  description: string | null;
  readOnly: boolean;
  destructive: boolean;
  /** Ticked for this agent (allowedTools null = every tool the role allows). */
  allowed: boolean;
}

/** GET /system/agents/{id}/tools: one entry per configured MCP service. */
export interface ToolConnection {
  service: string;
  title: string;
  /** false (and tools []) when no AI employee, its key is refused, or its token has no key of the service. */
  available: boolean;
  enabled: boolean;
  confirmWrites: boolean;
  /** null = every tool the role allows. */
  allowedTools: string[] | null;
  tools: ToolInfo[];
}

/** The AI employee state in words: Working / Key refused / Not tried yet / Not connected. */
export function serviceAccountStatus(sa: ServiceAccount | null | undefined): { label: string; css: string } {
  if (!sa || !sa.connected) return { label: 'Not connected', css: 'off' };
  if (sa.valid === true) return { label: 'Working', css: 'ok' };
  if (sa.valid === false) return { label: 'Key refused', css: 'warn' };
  return { label: 'Not tried yet', css: 'draft' };
}

/** A readable message for a failed AI employee / tools read. */
export function toolsErrorMessage(err: any, fallback: string): string {
  const body = err?.error || {};
  const message = String(body.message || '');
  if (err?.status === 404) return 'Not found: the agent does not exist, or the agent service does not have contract v1.5 (AI employee and tools) yet.';
  if (err?.status === 403) return 'No permission to read the AI employee and tools of this agent (system.agent.agent.get).';
  return message || fallback;
}

/** The channel of a conversation in words: TRIGGER = "Automation" (contract v1.6), none = "TEST". */
export function channelLabel(type: string | null | undefined): string {
  if (!type) return 'TEST';
  return type === 'TRIGGER' ? 'Automation' : type;
}

// ------------------------------------------------------------ triggers / automations (contract v1.6, read-only for support)

export type TriggerType = 'TIMER' | 'EVENT';
export type ScheduleKind = 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'EVERY_N_HOURS';
export type TriggerRunStatus = 'RUNNING' | 'OK' | 'NOTHING_NEW' | 'FAILED' | 'SKIPPED_LIMIT' | 'SKIPPED_OFF';

/** The fields of a schedule (contract v1.6 section 2, service TriggerSchedule.toJson); only those of its kind are set. */
export interface TriggerSchedule {
  /** ONCE: YYYY-MM-DD */
  date?: string | null;
  /** HH:mm (every kind but EVERY_N_HOURS) */
  time?: string | null;
  /** WEEKLY: 'MON' .. 'SUN' */
  days?: string[] | null;
  /** MONTHLY: 1-28 or 'last' */
  day?: number | string | null;
  /** EVERY_N_HOURS: 1-24 */
  n?: number | null;
  /** EVERY_N_HOURS: optional window, HH:mm */
  from?: string | null;
  to?: string | null;
}

/** GET /system/agents/{id}/triggers (and .../triggers/{triggerId}): TriggerDtos.TriggerResponse. */
export interface AgentTrigger {
  id: string;
  agentId: string;
  projectId: string;
  name: string;
  type: TriggerType | string;
  scheduleKind: ScheduleKind | string;
  schedule: TriggerSchedule;
  /** The schedule in the service's words, e.g. "Every day at 21:00"; preferred over scheduleLabel(). */
  scheduleText: string | null;
  timeZone: string | null;
  prompt: string;
  /** "service__tool" names; [] = no tools. */
  allowedTools: string[];
  /** Write tools that run without a person (never destructive). */
  preapprovedWrites: string[];
  /** Where the result goes; v1.6: { kind: 'DASHBOARD' }. */
  output: { kind: string } | null;
  enabled: boolean;
  /** ISO, UTC */
  nextRunAt: string | null;
  /** The next run as text in the trigger's time zone ("2026-11-01 09:00"). */
  nextRunLocal: string | null;
  lastRunAt: string | null;
  lastStatus: TriggerRunStatus | string | null;
  /** Failed runs in a row (3 switch the trigger off). */
  failuresInRow: number;
  /** Set when the trigger switched itself off. */
  disabledReason: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** GET /system/agents/{id}/triggers/{triggerId}/runs: TriggerDtos.TriggerRunResponse, newest first by seq. */
export interface TriggerRun {
  id: string;
  triggerId: string;
  agentId: string;
  seq: number;
  /** Started with "Run now". */
  manual: boolean;
  scheduledFor: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  status: TriggerRunStatus | string;
  /** The run as a TRIGGER conversation; null when it did not run (SKIPPED_*). */
  conversationId: string | null;
  /** The agent's full answer; null when the run failed or was skipped. */
  outputText: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  /** System scope: the full error (provider details too). */
  error: string | null;
}

/** A trigger as returned, with the lists and objects never missing. */
export function normalizeTrigger(raw: any): AgentTrigger {
  const r = raw || {};
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)) : []);
  const obj = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
  return {
    ...r,
    schedule: (obj(r.schedule) as TriggerSchedule) || {},
    scheduleText: r.scheduleText || null,
    allowedTools: list(r.allowedTools),
    preapprovedWrites: list(r.preapprovedWrites),
    output: obj(r.output) as { kind: string } | null,
    enabled: r.enabled === true,
    timeZone: r.timeZone ?? null,
    nextRunAt: r.nextRunAt ?? null,
    nextRunLocal: r.nextRunLocal ?? null,
    lastRunAt: r.lastRunAt ?? null,
    lastStatus: r.lastStatus ?? null,
    failuresInRow: typeof r.failuresInRow === 'number' ? r.failuresInRow : 0,
    disabledReason: r.disabledReason || null,
    createdBy: r.createdBy ?? null,
    createdAt: r.createdAt ?? null,
    updatedAt: r.updatedAt ?? null,
  };
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** 'SUN' / 'sunday' -> 'Sun' (the service sends MON..SUN). */
export function dayLabel(day: string): string {
  const s = String(day).trim().slice(0, 3).toLowerCase();
  return DAY_NAMES.find((d) => d.toLowerCase() === s) || String(day);
}

function ordinal(n: number): string {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return n + 'th';
  return n + (['th', 'st', 'nd', 'rd'][n % 10] || 'th');
}

/** The schedule in words, e.g. "Every day at 21:00" (contract v1.6, section 2); used when the service sends no scheduleText. */
export function scheduleLabel(kind: string | null | undefined, s: TriggerSchedule | null | undefined): string {
  const sc = s || {};
  const at = sc.time ? ' at ' + sc.time : '';
  switch (String(kind || '').toUpperCase()) {
    case 'ONCE':
      return 'Once' + (sc.date ? ' on ' + sc.date : '') + at;
    case 'DAILY':
      return 'Every day' + at;
    case 'WEEKLY': {
      const days = Array.isArray(sc.days) && sc.days.length ? sc.days.map(dayLabel).join(', ') : '(no day)';
      return 'Every ' + days + at;
    }
    case 'MONTHLY': {
      const d = sc.day;
      const day =
        d === null || d === undefined || d === '' ? '' : String(d).toLowerCase() === 'last' ? ' on the last day' : ' on the ' + ordinal(Number(d));
      return 'Every month' + day + at;
    }
    case 'EVERY_N_HOURS': {
      const n = Number(sc.n ?? 1);
      const window = sc.from && sc.to ? `, ${sc.from}-${sc.to}` : '';
      return (n === 1 ? 'Every hour' : `Every ${n} hours`) + window;
    }
    default:
      return kind ? String(kind) : '-';
  }
}

/** A run status in words with its badge colour. */
export const TRIGGER_RUN_STATUS: Record<string, { label: string; css: string }> = {
  RUNNING: { label: 'Running', css: 'draft' },
  OK: { label: 'Done', css: 'ok' },
  NOTHING_NEW: { label: 'Nothing new', css: 'draft' },
  FAILED: { label: 'Failed', css: 'warn' },
  SKIPPED_LIMIT: { label: 'Skipped: plan limit', css: 'warn' },
  SKIPPED_OFF: { label: 'Skipped: switched off', css: 'off' },
};

/** The status of a run (or a trigger's last status) in words; none = "Not run yet". */
export function runStatus(status: string | null | undefined): { label: string; css: string } {
  if (!status) return { label: 'Not run yet', css: 'off' };
  const key = String(status).toUpperCase();
  return TRIGGER_RUN_STATUS[key] || { label: key.toLowerCase(), css: 'draft' };
}

/** A readable message for a failed triggers / runs read. */
export function triggersErrorMessage(err: any, fallback: string): string {
  const body = err?.error || {};
  const message = String(body.message || '');
  if (err?.status === 404) return 'Not found: the agent does not exist, or the agent service does not have contract v1.6 (automations) yet.';
  if (err?.status === 403) return 'No permission to read the automations of this agent (system.agent.agent.get).';
  return message || fallback;
}

/** A playbook named in a message or in the test chat's `playbook` SSE event (contract v1.4). */
export interface PlaybookRef {
  key: string;
  title: string;
}

/** The playbook chips of a message: only assistant messages; an entry without a title shows its key. */
export function playbooksOf(m: Pick<ConversationMessage, 'role' | 'playbooksUsed'>): PlaybookRef[] {
  if (m.role !== 'ASSISTANT' || !Array.isArray(m.playbooksUsed)) return [];
  return m.playbooksUsed
    .filter((p) => p && (p.title || p.key))
    .map((p) => ({ key: String(p.key || ''), title: String(p.title || p.key) }));
}

// ------------------------------------------------------------ playbooks (contract v1.4, section 3)

export const PLAYBOOK_TITLE_MAX = 120;
export const PLAYBOOK_DESCRIPTION_MAX = 500;
export const PLAYBOOK_CONTENT_MAX = 20000;
export const PLAYBOOK_KEY_MAX = 60;
export const PLAYBOOK_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,59}$/;

export interface Playbook {
  id: string;
  agentId: string;
  key: string;
  title: string;
  /** "When to use": tells the model when to read the playbook. */
  description: string;
  /** Markdown; null in the list (only the single read carries it). */
  content: string | null;
  enabled: boolean;
  sortOrder: number;
  /** Length of the content. */
  chars: number;
  createdAt?: string;
  updatedAt?: string;
}

/** POST: title, description, content required; key made from the title when missing. PUT: null fields stay unchanged. */
export interface PlaybookRequest {
  key?: string | null;
  title?: string | null;
  description?: string | null;
  content?: string | null;
  enabled?: boolean | null;
  sortOrder?: number | null;
}

/** Line ends normalised to \n and trimmed, as the service stores the content. */
export function normalizePlaybookContent(content: string | null | undefined): string {
  return (content || '').replace(/\r\n?/g, '\n').trim();
}

/** The form check before a save, with the same rules as the service (PLAYBOOK_INVALID / PLAYBOOK_TOO_LONG); null = valid. */
export function playbookFormProblem(
  form: { key?: string | null; title?: string | null; description?: string | null; content?: string | null },
  isNew: boolean
): string | null {
  const title = (form.title || '').trim();
  const description = (form.description || '').trim();
  const content = normalizePlaybookContent(form.content);
  const key = (form.key || '').trim();
  if (!title) return 'The title is required.';
  if (title.length > PLAYBOOK_TITLE_MAX) return `The title can have at most ${PLAYBOOK_TITLE_MAX} characters.`;
  if (!description) return '"When to use" is required: it tells the agent when to read this playbook.';
  if (description.length > PLAYBOOK_DESCRIPTION_MAX) return `"When to use" can have at most ${PLAYBOOK_DESCRIPTION_MAX} characters.`;
  if (!content) return 'The content is required.';
  if (content.length > PLAYBOOK_CONTENT_MAX) return 'The content can have at most 20,000 characters.';
  if (isNew && key && !PLAYBOOK_KEY_PATTERN.test(key)) {
    return 'The key may use a-z, 0-9, "-" and "_", must start with a letter or digit and can have at most 60 characters. Leave it empty to make it from the title.';
  }
  return null;
}

/** A readable message for a failed playbook call, by the service error code. */
export function playbookErrorMessage(err: any, fallback: string): string {
  const body = err?.error || {};
  const code = String(body.code || body.error || '');
  const message = String(body.message || '');
  const has = (c: string) => code === c || message.includes(c);
  if (has('PLAYBOOK_KEY_TAKEN')) return 'This key is already used by another playbook of this agent. Choose another key, or leave it empty.';
  if (has('PLAYBOOK_TOO_LONG')) return 'The content is longer than 20,000 characters. Shorten it, or split it into two playbooks.';
  if (has('PLAYBOOK_INVALID')) {
    const detail = message && message !== 'PLAYBOOK_INVALID' ? ` (${message})` : '';
    return `The playbook is not valid${detail}. Title (1-120 characters), when to use (1-500) and content are required; the key may use a-z, 0-9, "-" and "_".`;
  }
  if (has('PLAN_LIMIT')) return 'The agent already has as many playbooks as its project plan allows. Raise "Max playbooks per agent" on the Project plans page, or delete one.';
  if (has('FEATURE_NOT_IN_PLAN')) return 'The project plan does not allow playbooks. Allow them on the Project plans page first.';
  if (err?.status === 404) return 'The playbook or the agent was not found. Reload the list.';
  return message || fallback;
}

export type AttachmentKind = 'IMAGE' | 'AUDIO' | 'DOCUMENT';

export interface MessageAttachment {
  attachmentId: string;
  kind: AttachmentKind;
  contentType: string;
  /** Contract v1.3 documents: the visitor's file name, the file size and the pages (or sheets). */
  fileName?: string | null;
  size?: number | null;
  pages?: number | null;
}

/** `1536` -> `1.5 KB` */
export function fileSizeLabel(bytes: number | null | undefined): string {
  const b = Math.max(0, Number(bytes) || 0);
  if (b < 1024) return b + ' B';
  const units = ['KB', 'MB', 'GB'];
  let value = b / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return (value < 10 ? value.toFixed(1).replace(/\.0$/, '') : String(Math.round(value))) + ' ' + units[unit];
}

// ------------------------------------------------------------ knowledge files (contract v1.2, section 4)

export type KnowledgeSourceType = 'TEXT' | 'FILE';
export type KnowledgeStatus = 'PENDING' | 'INDEXING' | 'READY' | 'FAILED';

export interface KnowledgeDocument {
  id: string;
  agentId: string;
  /** In the contract; the service DTO does not send it (yet). */
  projectId?: string;
  title: string;
  sourceType: KnowledgeSourceType;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  /** Only on the single-document read. */
  text?: string | null;
  status: KnowledgeStatus;
  error: string | null;
  chunkCount: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface KnowledgeTextRequest {
  title: string;
  text: string;
}

/** text only for TEXT documents; a change triggers a re-index. */
export interface KnowledgeUpdateRequest {
  title: string;
  text?: string | null;
}

export interface PageResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

/** Limits of one project (system scope). A project without a stored plan uses the defaults. */
export interface ProjectPlan {
  projectId: string;
  maxAgents: number;
  maxMessagesPerMonth: number;
  /** llm_model ids the project may use; null = every enabled model. */
  allowedModelIds: string[] | null;
  enabled: boolean;
  updatedAt?: string | null;
  allowVoiceInput?: boolean;
  allowVoiceOutput?: boolean;
  allowImages?: boolean;
  allowKnowledgeFiles?: boolean;
  /** Contract v1.3: visitor documents in the chat. */
  allowDocuments?: boolean;
  maxKnowledgeFiles?: number;
  maxKnowledgeMb?: number;
  maxAudioSecondsPerMonth?: number;
  /** Contract v1.4: playbooks (default true) and how many one agent may have (default 10). */
  allowPlaybooks?: boolean;
  maxPlaybooksPerAgent?: number;
  /** Contract v1.5: MCP tools (default true) and how many enabled tool connections one agent may have (default 5). */
  allowTools?: boolean;
  maxToolConnectionsPerAgent?: number;
  /** Contract v1.6: triggers (default true), how many one agent may have (default 5) and runs per month of the project (default 300). */
  allowTriggers?: boolean;
  maxTriggersPerAgent?: number;
  maxTriggerRunsPerMonth?: number;
}

export interface ProjectPlanRequest {
  maxAgents: number;
  maxMessagesPerMonth: number;
  allowedModelIds: string[] | null;
  enabled: boolean;
  allowVoiceInput?: boolean;
  allowVoiceOutput?: boolean;
  allowImages?: boolean;
  allowKnowledgeFiles?: boolean;
  /** Contract v1.3: visitor documents in the chat. */
  allowDocuments?: boolean;
  maxKnowledgeFiles?: number;
  maxKnowledgeMb?: number;
  maxAudioSecondsPerMonth?: number;
  /** Contract v1.4: playbooks (default true) and how many one agent may have (default 10). */
  allowPlaybooks?: boolean;
  maxPlaybooksPerAgent?: number;
  /** Contract v1.5: MCP tools (default true) and how many enabled tool connections one agent may have (default 5). */
  allowTools?: boolean;
  maxToolConnectionsPerAgent?: number;
  /** Contract v1.6: triggers (default true), how many one agent may have (default 5) and runs per month of the project (default 300). */
  allowTriggers?: boolean;
  maxTriggersPerAgent?: number;
  maxTriggerRunsPerMonth?: number;
}

/**
 * Every AI Assistant option is part of the Web Studio version the customer bought (Dr. Amer, 2026-10-21; service V6,
 * documents V7): allowed by default. A plan row only switches one off as a support exception.
 */
export const PLAN_DEFAULTS = {
  maxAgents: 3,
  maxMessagesPerMonth: 2000,
  allowVoiceInput: true,
  allowVoiceOutput: true,
  allowImages: true,
  allowKnowledgeFiles: true,
  allowDocuments: true,
  allowPlaybooks: true,
  allowTools: true,
  allowTriggers: true,
  maxKnowledgeFiles: 10,
  maxKnowledgeMb: 20,
  maxAudioSecondsPerMonth: 600,
  maxPlaybooksPerAgent: 10,
  maxToolConnectionsPerAgent: 5,
  maxTriggersPerAgent: 5,
  maxTriggerRunsPerMonth: 300,
} as const;

export type PlanFeatureKey = 'allowImages' | 'allowDocuments' | 'allowVoiceInput' | 'allowVoiceOutput' | 'allowKnowledgeFiles' | 'allowPlaybooks' | 'allowTools' | 'allowTriggers';
export type PlanLimitKey = 'maxKnowledgeFiles' | 'maxKnowledgeMb' | 'maxAudioSecondsPerMonth' | 'maxPlaybooksPerAgent' | 'maxToolConnectionsPerAgent' | 'maxTriggersPerAgent' | 'maxTriggerRunsPerMonth';

/** The v1.2 - v1.6 feature switches of a plan, in display order. */
export const PLAN_FEATURES: { key: PlanFeatureKey; label: string }[] = [
  { key: 'allowImages', label: 'Images' },
  { key: 'allowDocuments', label: 'Documents' },
  { key: 'allowVoiceInput', label: 'Voice input' },
  { key: 'allowVoiceOutput', label: 'Voice reply' },
  { key: 'allowKnowledgeFiles', label: 'Knowledge files' },
  { key: 'allowPlaybooks', label: 'Playbooks' },
  { key: 'allowTools', label: 'Tools' },
  { key: 'allowTriggers', label: 'Automations' },
];

/** The v1.2 / v1.4 / v1.5 / v1.6 number limits of a plan. */
export const PLAN_LIMITS: { key: PlanLimitKey; label: string; hint: string }[] = [
  { key: 'maxKnowledgeFiles', label: 'Max knowledge files', hint: 'Files and text pages per agent.' },
  { key: 'maxKnowledgeMb', label: 'Max knowledge size (MB)', hint: 'Total size of the knowledge files.' },
  { key: 'maxAudioSecondsPerMonth', label: 'Audio seconds per month', hint: 'Voice input of the whole project.' },
  { key: 'maxPlaybooksPerAgent', label: 'Max playbooks per agent', hint: 'Enabled or not; 0 = no playbook can be added.' },
  { key: 'maxToolConnectionsPerAgent', label: 'Max tool connections per agent', hint: 'Switched-on services (e.g. Leads) per agent.' },
  { key: 'maxTriggersPerAgent', label: 'Max automations per agent', hint: 'Timed tasks (triggers) per agent, on or off.' },
  { key: 'maxTriggerRunsPerMonth', label: 'Automation runs per month', hint: 'Runs of the whole project; each run also counts as messages.' },
];

/** A plan with every v1.2 field filled (an older service leaves them out). */
export function withPlanDefaults(plan: ProjectPlan): ProjectPlan {
  const d = PLAN_DEFAULTS;
  return {
    ...plan,
    allowVoiceInput: plan.allowVoiceInput ?? d.allowVoiceInput,
    allowVoiceOutput: plan.allowVoiceOutput ?? d.allowVoiceOutput,
    allowImages: plan.allowImages ?? d.allowImages,
    allowKnowledgeFiles: plan.allowKnowledgeFiles ?? d.allowKnowledgeFiles,
    allowDocuments: plan.allowDocuments ?? d.allowDocuments,
    allowPlaybooks: plan.allowPlaybooks ?? d.allowPlaybooks,
    maxPlaybooksPerAgent: plan.maxPlaybooksPerAgent ?? d.maxPlaybooksPerAgent,
    allowTools: plan.allowTools ?? d.allowTools,
    maxToolConnectionsPerAgent: plan.maxToolConnectionsPerAgent ?? d.maxToolConnectionsPerAgent,
    allowTriggers: plan.allowTriggers ?? d.allowTriggers,
    maxTriggersPerAgent: plan.maxTriggersPerAgent ?? d.maxTriggersPerAgent,
    maxTriggerRunsPerMonth: plan.maxTriggerRunsPerMonth ?? d.maxTriggerRunsPerMonth,
    maxKnowledgeFiles: plan.maxKnowledgeFiles ?? d.maxKnowledgeFiles,
    maxKnowledgeMb: plan.maxKnowledgeMb ?? d.maxKnowledgeMb,
    maxAudioSecondsPerMonth: plan.maxAudioSecondsPerMonth ?? d.maxAudioSecondsPerMonth,
  };
}

export interface ProjectOption {
  projectId: string;
  name: string;
}

/** Permission keys checked by the service's @RequiresPermission annotations. */
export const AGENT_PERMISSIONS = {
  providerList: 'system.agent.provider.list',
  providerGet: 'system.agent.provider.get',
  providerCreate: 'system.agent.provider.create',
  providerUpdate: 'system.agent.provider.update',
  providerApiKey: 'system.agent.provider.apikey',
  providerDelete: 'system.agent.provider.delete',
  modelList: 'system.agent.model.list',
  modelGet: 'system.agent.model.get',
  modelCreate: 'system.agent.model.create',
  modelUpdate: 'system.agent.model.update',
  modelDelete: 'system.agent.model.delete',
  agentList: 'system.agent.agent.list',
  agentGet: 'system.agent.agent.get',
  agentCreate: 'system.agent.agent.create',
  agentUpdate: 'system.agent.agent.update',
  agentDelete: 'system.agent.agent.delete',
  agentTest: 'system.agent.agent.test',
  conversationList: 'system.agent.conversation.list',
  conversationGet: 'system.agent.conversation.get',
  planList: 'system.agent.plan.list',
  planGet: 'system.agent.plan.get',
  planUpdate: 'system.agent.plan.update',
  capabilityList: 'system.agent.capability.list',
  capabilityCreate: 'system.agent.capability.create',
  capabilityUpdate: 'system.agent.capability.update',
  capabilityDelete: 'system.agent.capability.delete',
  knowledgeList: 'system.agent.knowledge.list',
  knowledgeCreate: 'system.agent.knowledge.create',
  knowledgeUpdate: 'system.agent.knowledge.update',
  knowledgeDelete: 'system.agent.knowledge.delete',
  playbookList: 'system.agent.playbook.list',
  playbookCreate: 'system.agent.playbook.create',
  playbookUpdate: 'system.agent.playbook.update',
  playbookDelete: 'system.agent.playbook.delete',
} as const;
